import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { useHeaderContext } from '@jouzen/ecom-components/layout';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { useCarouselContext } from '@jouzen/ecom-components/v4/Carousel';
import { cx } from 'class-variance-authority';
import { useInView } from 'framer-motion';
import {
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  type SourceHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import type { VideoProps } from '@/app/components/Video/types/VideoProps';
import Video from '@/app/components/Video/Video';

export enum HeroCarouselSlideVariant {
  FullBleed = 'full-bleed',
  RingFront = 'ring-front',
  RingAngle = 'ring-angle',
  RingSpin = 'ring-spin',
}

type SlideWrapperProps = PropsWithChildren<ComponentPropsWithoutRef<'div'>>;

interface BaseSlideProps {
  readonly alt: MessageKey;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly isActive?: boolean;
}

interface FullBleedSlideProps extends BaseSlideProps {
  readonly srcSet: SourceHTMLAttributes<HTMLSourceElement>[];
}

interface RingFrontSlideProps extends BaseSlideProps {
  readonly src: string;
}

interface RingAngleSlideProps extends BaseSlideProps {
  readonly bgColor?: string | undefined;
  readonly src: string;
}

interface RingImageProps extends ComponentPropsWithoutRef<'div'> {
  readonly alt: MessageKey;
  readonly src: string;
}

interface RingVideoProps extends VideoProps, BaseSlideProps {
  readonly altImageSrc?: string;
  readonly isActive?: boolean;
}

type FullBleedVariant = {
  readonly variant: HeroCarouselSlideVariant.FullBleed;
} & FullBleedSlideProps;

type RingVideoVariant = {
  readonly variant: HeroCarouselSlideVariant.RingSpin;
} & RingVideoProps;

type RingFrontVariant = {
  readonly variant: HeroCarouselSlideVariant.RingFront;
} & RingFrontSlideProps;

type RingAngleVariant = {
  readonly variant: HeroCarouselSlideVariant.RingAngle;
} & RingAngleSlideProps;

type SlideContentsProps =
  | FullBleedVariant
  | RingVideoVariant
  | RingFrontVariant
  | RingAngleVariant;

export interface HeroCarouselSlideProps
  extends ComponentPropsWithoutRef<'div'> {
  readonly contents: SlideContentsProps;
  readonly headerInverse?: boolean;
  readonly index?: number;
}

const SlideWrapper = ({ className, children, ...props }: SlideWrapperProps) => {
  return (
    <div
      className={cx('absolute inset-0', className)}
      data-cy="hero-carousel-slide"
      {...props}
    >
      {children}
    </div>
  );
};

const RingImage = ({ alt, className, src }: RingImageProps) => (
  <div
    className={cx(
      'absolute top-1/2 left-1/2 w-3/4 max-w-[315px] -translate-1/2 md:w-[55%] lg:max-w-[520px]',
      className,
    )}
  >
    <Image
      className="object-contain object-center"
      fill
      localizedAlt={alt}
      priority
      sizes="(max-width: 420px) 75vw, (max-width: 767px) 315px, (max-width: 1023px) 30vw, (max-width: 1279px) 35vw, 520px"
      src={src}
    />
  </div>
);

const FullBleedSlide = ({ alt, srcSet }: FullBleedSlideProps) => {
  return (
    <SlideWrapper data-cy="full-bleed-slide">
      <ArtDirectionImage
        className="absolute size-full object-cover object-center"
        fill
        localizedAlt={alt}
        priority
        srcSet={srcSet.map((_srcSet) => ({
          ..._srcSet,
          sizes:
            '(max-width: 767px) 100vw, (max-width: 1023px) 55vw, (max-width: 1279px) 62vw, 66vw',
        }))}
      />
    </SlideWrapper>
  );
};

const RingFrontSlide = ({ alt, src }: RingFrontSlideProps) => {
  return (
    <SlideWrapper className="bg-[#E4E0DB]" data-cy="ring-front-slide">
      <RingImage alt={alt} className="aspect-square" src={src} />
    </SlideWrapper>
  );
};

const RingVideoSlide = ({
  alt,
  altImageSrc,
  contentTitle,
  location,
  isActive = false,
  src,
  type,
}: RingVideoProps) => {
  const [currentTime, setCurrentTime] = useState<number>(0);

  useEffect(() => {
    // Restart the video when the slide becomes active
    if (isActive) setCurrentTime(0);
  }, [isActive]);

  return (
    <SlideWrapper data-cy="ring-video-slide">
      <Video
        altImage={{
          alt,
          priority: true,
          src: altImageSrc,
          width: 609,
          height: 457,
        }}
        autoPlay
        className="relative size-full object-cover"
        contentTitle={contentTitle}
        controls={false}
        currentTime={currentTime}
        key={src}
        location={location}
        loop
        muted
        playsInline
        playVideo={isActive}
        src={src}
        type={type}
      />
    </SlideWrapper>
  );
};

const RingAngleSlide = ({ alt, bgColor, src }: RingAngleSlideProps) => {
  return (
    <SlideWrapper
      data-cy="ring-angle-slide"
      style={{ backgroundColor: bgColor }}
    >
      <RingImage alt={alt} className="aspect-[11/10]" src={src} />
    </SlideWrapper>
  );
};

const SlideContents = ({
  alt,
  isActive = false,
  ...props
}: SlideContentsProps) => {
  const baseProps: BaseSlideProps = { alt, isActive };
  switch (props.variant) {
    case HeroCarouselSlideVariant.FullBleed: {
      const { srcSet } = props;
      return <FullBleedSlide {...baseProps} srcSet={srcSet} />;
    }
    case HeroCarouselSlideVariant.RingSpin: {
      return <RingVideoSlide {...baseProps} {...props} isActive={isActive} />;
    }
    case HeroCarouselSlideVariant.RingFront: {
      const { src } = props;
      return <RingFrontSlide {...baseProps} src={src} />;
    }
    case HeroCarouselSlideVariant.RingAngle: {
      const { bgColor, src } = props;
      return <RingAngleSlide {...baseProps} bgColor={bgColor} src={src} />;
    }
  }
};

const HeroCarouselSlide = ({
  contents,
  headerInverse = false,
  index,
  ...props
}: HeroCarouselSlideProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { setInverse } = useHeaderContext()!;
  const isInView = useInView(wrapperRef, { amount: 0.8 });
  const isTabletBreakpoint = useMediaQuery(
    `(max-width: ${BREAKPOINTS.large - 1}px)`,
  );
  const { activeIndex } = useCarouselContext();

  useEffect(() => {
    if (isTabletBreakpoint) {
      if (isInView) {
        setInverse(headerInverse);
      }
    } else {
      setInverse(false);
    }
  }, [headerInverse, isInView, isTabletBreakpoint, setInverse]);

  return (
    <div className="relative size-full" ref={wrapperRef} {...props}>
      <SlideContents {...contents} isActive={activeIndex === index} />
    </div>
  );
};

export default HeroCarouselSlide;
